{
  "id": "ibex",
  "name": "Ibex",
  "kind": "core",
  "url": "https://github.com/lowRISC/ibex",
  "homepage": "https://www.lowrisc.org",
  "origin": "lowRISC, ETH Zurich",
  "authors": [
    "lowRISC",
    "PULP platform, ETH Zurich and University of Bologna"
  ],
  "started": 2017,
  "language": "SystemVerilog",
  "license": "Apache-2.0",
  "isa": "RV32IMCB, RV32EC",
  "archived": false,
  "sources": [
    {
      "label": "Ibex README",
      "url": "https://github.com/lowRISC/ibex"
    },
    {
      "label": "Zero-riscy, PATMOS 2017",
      "url": "https://doi.org/10.1109/PATMOS.2017.8106976"
    },
    {
      "label": "OpenTitan",
      "url": "https://opentitan.org"
    },
    {
      "label": "Ibex documentation",
      "url": "https://ibex-core.readthedocs.io"
    },
    {
      "label": "Ibex simple system",
      "url": "https://github.com/lowRISC/ibex/tree/master/examples/simple_system"
    },
    {
      "label": "RISC-V Debug Specification",
      "url": "https://riscv.org/technical/specifications/"
    }
  ],
  "blocks": [
    {
      "k": "h",
      "text": "Overview"
    },
    {
      "k": "table",
      "headers": [
        "Specification",
        "Value"
      ],
      "rows": [
        [
          "ISA support",
          "RV32IMCB, RV32EC"
        ],
        [
          "Microarchitecture",
          "Two-stage in-order pipeline, with an optional writeback stage and single-cycle multiplier"
        ],
        [
          "Performance density",
          "0.904\u20133.13 CoreMark/MHz depending on configuration"
        ],
        [
          "Resource footprint",
          "16.85\u201366.02 kGE in Yosys synthesis, about 15\u201361 kGE with commercial tools"
        ],
        [
          "Clock domain",
          "No fixed fMAX; frequency depends on configuration and target technology"
        ]
      ]
    },
    {
      "k": "p",
      "text": "Ibex is a small 32-bit RISC-V core written in SystemVerilog, a language for describing hardware. A core is the part of a chip that executes instructions. Ibex is designed for embedded control, the work of small processors that manage devices inside larger systems."
    },
    {
      "k": "p",
      "text": "Ibex supports the base integer instruction set or its reduced embedded version, plus the M, C and B extensions for multiplication, compressed instructions and bit manipulation. Its configurations range from a minimal RV32EC build to faster builds with a single-cycle multiplier and memory protection. The Ibex Demo System targets the Digilent Arty A7 FPGA board with OpenOCD and GDB over USB, providing a real FPGA path for the core."
    },
    {
      "k": "h",
      "text": "Microarchitecture"
    },
    {
      "k": "photo",
      "src": "/images/processors/ibex/blockdiagram.svg",
      "text": "Ibex core block diagram showing instruction fetch, decode and execute, register file, load store unit and controllers.",
      "credit": "Ibex documentation (Apache-2.0)",
      "href": "https://github.com/lowRISC/ibex/tree/master/doc/03_reference/images"
    },
    {
      "k": "photo",
      "src": "/images/processors/ibex/de_ex_stage.svg",
      "text": "Decode and execute stage of Ibex.",
      "credit": "Ibex documentation (Apache-2.0)",
      "href": "https://github.com/lowRISC/ibex/tree/master/doc/03_reference/images"
    },
    {
      "k": "p",
      "text": "Ibex implements a two-stage pipeline consisting of instruction fetch and execution. This pipeline reduces control complexity and limits the need for hazard management compared with deeper pipeline designs. An optional third writeback stage can be enabled to separate register updates from execution, improving timing closure at the cost of additional pipeline latency."
    },
    {
      "k": "p",
      "text": "The processor is highly configurable, allowing implementations to select supported instruction set extensions, multiplier architecture, branch target ALU, protected memory regions and instruction cache. These options directly affect the resulting hardware implementation, including its supported ISA, resource consumption and performance. Consequently, specifications and benchmark figures must be interpreted in the context of the particular configuration being evaluated."
    },
    {
      "k": "table",
      "headers": [
        "Configuration",
        "micro",
        "small",
        "maxperf",
        "maxperf-pmp-bmfull"
      ],
      "rows": [
        [
          "ISA",
          "RV32EC",
          "RV32IMC",
          "RV32IMC",
          "RV32IMCB"
        ],
        [
          "Multiplier",
          "None",
          "3 cycles",
          "1 cycle",
          "1 cycle"
        ],
        [
          "Branch target ALU",
          "No",
          "No",
          "Yes",
          "Yes"
        ],
        [
          "Writeback stage",
          "No",
          "No",
          "Yes",
          "Yes"
        ],
        [
          "PMP regions",
          "None",
          "None",
          "None",
          "16"
        ],
        [
          "CoreMark/MHz",
          "0.904",
          "2.47",
          "3.13",
          "3.13"
        ],
        [
          "Area, Yosys",
          "16.85 kGE",
          "26.60 kGE",
          "32.48 kGE",
          "66.02 kGE"
        ],
        [
          "Verification",
          "red",
          "green",
          "amber",
          "amber"
        ]
      ]
    },
    {
      "k": "h",
      "text": "Memory and register map"
    },
    {
      "k": "p",
      "text": "The memory map is defined by the surrounding platform rather than by the Ibex core itself. Ibex receives its boot address through configuration and accesses instruction and data memory through interfaces connected to the system. The reference design includes a platform called the Ibex Simple System, which provides an example memory map as shown below."
    },
    {
      "k": "table",
      "headers": [
        "Address",
        "Region"
      ],
      "rows": [
        [
          "0x100000 to 0x1fffff",
          "1 MB of memory holding both instructions and data"
        ],
        [
          "0x20000",
          "ASCII output, writes go to the simulation log"
        ],
        [
          "0x20008",
          "Simulator halt, write 1 to stop"
        ],
        [
          "0x30000",
          "Timer mtime and mtimeh"
        ],
        [
          "0x30008",
          "Timer compare mtimecmp and mtimecmph"
        ]
      ]
    },
    {
      "k": "callout",
      "variant": "note",
      "text": "The simple system starts executing at 0x100080 and puts the exception handler at 0x100000. A different platform, OpenTitan for example, uses a completely different map."
    },
    {
      "k": "h",
      "text": "Physical implementation"
    },
    {
      "k": "p",
      "text": "Ibex area is quoted in gate equivalents. The configurations range from about 15 to 61 thousand gate equivalents with commercial synthesis."
    },
    {
      "k": "table",
      "headers": [
        "Metric",
        "Value"
      ],
      "rows": [
        [
          "Area, Yosys",
          "16.85 to 66.02 kGE depending on configuration"
        ],
        [
          "Area, commercial estimate",
          "About 15 to 61 kGE"
        ],
        [
          "Process node",
          "Chosen by the platform, OpenTitan uses its own"
        ],
        [
          "Silicon",
          "OpenTitan chips use Ibex as the main processor"
        ]
      ]
    },
    {
      "k": "h",
      "text": "Identifying the part"
    },
    {
      "k": "p",
      "text": "Ibex is soft IP and the main processor in OpenTitan, the open source security chip project. OpenTitan product markings identify the vendor and the product, and the core is documented in the platform documentation and its boot ROM layout."
    },
    {
      "k": "table",
      "headers": [
        "Where it appears",
        "How to confirm"
      ],
      "rows": [
        [
          "OpenTitan chips",
          "Product documentation and the boot ROM layout"
        ],
        [
          "FPGA boards",
          "Bitstream and platform files from the vendor or project"
        ],
        [
          "Research platforms",
          "Release notes and the configured ISA string"
        ]
      ]
    },
    {
      "k": "h",
      "text": "Disassembly and debugging"
    },
    {
      "k": "p",
      "text": "A maxperf build targets rv32imc with the bit manipulation extensions. The micro configuration targets RV32EC, which changes the register count and the ABI."
    },
    {
      "k": "code",
      "title": "Tools",
      "code": "riscv32-unknown-elf-readelf -A firmware.elf\nriscv32-unknown-elf-objdump -d -M no-aliases firmware.elf\nllvm-objdump -d --mattr=+m,+c,+zba,+zbb,+zbc,+zbs firmware.elf",
      "comments": [
        null,
        null,
        null
      ]
    },
    {
      "k": "table",
      "headers": [
        "Item",
        "Value"
      ],
      "rows": [
        [
          "ISA",
          "RV32IMCB or RV32EC depending on configuration"
        ],
        [
          "Compiler flags",
          "-march=rv32imc_zba_zbb_zbc_zbs -mabi=ilp32, or -march=rv32ec -mabi=ilp32e"
        ],
        [
          "Debug transport",
          "RISC-V Debug 0.13, through an external debug module such as pulp-platform/riscv-dbg"
        ],
        [
          "Debug parameters",
          "DmBaseAddr, DmHaltAddr, DmExceptionAddr, configurable at integration"
        ],
        [
          "Boot",
          "boot_addr_i from the platform, 0x100080 in the simple system"
        ],
        [
          "misa (0x301)",
          "ISA and enabled extensions"
        ],
        [
          "mvendorid (0xF11)",
          "JEDEC vendor ID, read-only"
        ],
        [
          "marchid (0xF12)",
          "microarchitecture ID, read-only"
        ],
        [
          "mimpid (0xF13)",
          "implementation version, read-only"
        ],
        [
          "mhartid (0xF14)",
          "hart that runs the code, read-only"
        ]
      ]
    },
    {
      "k": "h",
      "text": "History"
    },
    {
      "k": "p",
      "text": "Ibex began as zero-riscy, a core from the PULP platform, a research project of ETH Zurich and the University of Bologna that develops low-power processors. zero-riscy was presented in 2017 as a smaller relative of the RI5CY core. The design was then contributed to lowRISC, a nonprofit in Cambridge, United Kingdom, which renamed it Ibex and continues to develop it."
    },
    {
      "k": "h",
      "text": "Where it is used"
    },
    {
      "k": "p",
      "text": "OpenTitan, an open source design for security chips that check the software a computer starts, uses Ibex as its processor. Ibex also appears in research chips and in designs that need a small core with a strong verification record."
    }
  ],
  "seoTitle": "Ibex, 32-bit RISC-V core, configurations and memory map",
  "seoDescription": "Ibex is a two-stage 32-bit RISC-V core with micro, small and maxperf configurations, CoreMark and area figures, OpenTitan silicon and a simple system memory map."
}
